/**
 * Messenger facade — the only way UI code sends anything (protocol v2).
 * Each request becomes an app payload (stable id + type + body) that is
 * ratchet-encrypted, sealed per recipient, committed to the durable outbox and
 * published. UI code never touches sessions, envelopes or relays directly.
 */
import { createEventId, eventIdOwnedBy, type Account, type Contact, type PublishOutcome } from "@veto/domain";
import { newPayload, sendPayload, type SendContext } from "./wire";
import { messagingDeviceIdHex } from "./sessionDevices";

export type SendRequest = {
  /** UI conversation (direct or group) the send belongs to. */
  conversationId: string;
  recipients: Contact[];
  contentType: number;
  body: string;
  /** Stable message id (shared with recipients); defaults to a fresh id. */
  eventId?: string;
  createdAt?: number;
  /** true when a stored message row tracks this send's delivery status. */
  tracksMessage?: boolean;
};

export type Messenger = {
  account: Account;
  /** Our device id as peers see it (derived from the identity key). */
  selfDevice: string;
  send: (req: SendRequest) => Promise<PublishOutcome>;
  /** Ed25519 signature with our identity key (group-key authorization). */
  signAsIdentity: (message: Uint8Array) => Uint8Array;
};

/** Upper bound for one message body so a sealed event stays under common relay limits. */
export const MAX_BODY_BYTES = 24_000;

export function bodyFitsOneEnvelope(body: string): boolean {
  return new TextEncoder().encode(body).length <= MAX_BODY_BYTES;
}

export function createMessenger(ctx: SendContext): Messenger {
  const name = ctx.account.displayName.trim().slice(0, 64) || undefined;
  return {
    account: ctx.account,
    selfDevice: messagingDeviceIdHex(ctx.account.messagingPublicKeyHex),
    send: (req) => {
      const self = ctx.account.messagingPublicKeyHex;
      const id = req.eventId ?? createEventId(self);
      // Receivers drop ids we do not own (review L2) — fail loudly here instead.
      if (!eventIdOwnedBy(id, self)) throw new Error("message id not minted by this account");
      const payload = newPayload(req.contentType, req.body, id, req.createdAt);
      return sendPayload(ctx, {
        conversationId: req.conversationId,
        recipients: req.recipients,
        payload: name ? { ...payload, n: name } : payload,
        tracksMessage: req.tracksMessage ?? false,
      });
    },
    signAsIdentity: (m) => ctx.store.identity.sign(m),
  };
}
